import { EventEmitter, Injectable } from '@angular/core';
import { BotidService } from '@shared-angular/services/ragai/botid.service';
import { KeyValEntry } from '@shared/KeyValStore';
import { audioTextSchema } from '@shared/ragai.schemas';
import { z } from 'zod';
import { AudioToTextService } from '../audio-to-text/audio-to-text.service';
import { BotService } from '../bot.service';
import { EventManagerService } from '../event-manager/event-manager.service';
import { HelperManagerService } from '../helper-manager.service';
import { KeyValService } from '../key-val.service';

// onClick bleibt eine echte Funktionsreferenz (kein eval wie bei BUTTONCARDBUTTON) - Footer-Buttons
// werden nicht persistiert, sondern gelten nur für die laufende Session.
export type AvatarFooterButton = {
  text: string,
  className?: string,
  onClick: () => void | Promise<void>
}

/**
 * Steuert Hintergrundbild und Sprachausgabe des Video-/LiveAvatars (`RAGAI.VIDEO_AVATAR`).
 * @remarks
 * {@link stop} ist die Barge-in-Funktion: bricht sowohl das Bot-Denken (Server-seitiger Run) als auch eine
 * laufende Sprachausgabe des LiveAvatars sofort ab - z. B. wenn der Nutzer den Avatar per Button unterbricht
 * oder zu sprechen beginnt, während der Avatar noch redet.
 * Die Sprech-Zustände des Avatars (`RAGAI.AVATAR.SPEECH.thinking`/`.play`/`.done`) werden nicht von dieser
 * Klasse selbst ausgelöst, sondern über `RAGAI.EVENTS.listenTo(...)` beobachtet, um z. B. per {@link setText}
 * einen passenden Status-Text im Avatar-Video anzuzeigen.
 * {@link stop} kennt den aktuell aktiven Avatar-Provider nicht selbst - das eigentliche Abbrechen der
 * Sprachausgabe wird per `RAGAI.AVATAR.INTERRUPT`-Event an die jeweilige Provider-Komponente delegiert
 * (LiveAvatarComponent/DIdAvatarComponent), die dort providerspezifisch reagiert.
 */
@Injectable({
  providedIn: 'root'
})
export class VideoAvatarManagerService {
  private key = 'videoAvatarBackground';
  /** Feuert mit dem gespeicherten KeyVal-Eintrag, sobald ein neues Hintergrundbild gesetzt wurde. */
  public onBackgroundUpdate: EventEmitter<KeyValEntry> = new EventEmitter<KeyValEntry>();
  /** Feuert mit dem gelöschten KeyVal-Eintrag, sobald das Hintergrundbild entfernt wurde. */
  public onBackgroundDelete: EventEmitter<KeyValEntry> = new EventEmitter<KeyValEntry>();
  private footerButtons: AvatarFooterButton[] = [];
  /** Feuert mit der aktuellen (kompletten) Footer-Button-Liste, sobald ein Button hinzugefügt oder die Liste geleert wurde. */
  public onFooterButtonsUpdate: EventEmitter<AvatarFooterButton[]> = new EventEmitter<AvatarFooterButton[]>();

  constructor(
    private keyvalManager: KeyValService,
    private helperManager: HelperManagerService,
    private attService: AudioToTextService,
    private botService: BotService,
    private botidService: BotidService,
    private eventManager: EventManagerService
  ) { }

  /**
   * Überschreibt den angezeigten Text im Video-Avatar (`avatarbot_overrideText`).
   * @param text Der anzuzeigende Text, oder ein Objekt gemäß `audioTextSchema`.
   */
  public setText(text: string | z.input<typeof audioTextSchema>): void {
    const parsed = audioTextSchema.safeParse(text);
    if (parsed.success && typeof text === 'object') {
      text = parsed.data.text;
    }
    window.avatarbot_overrideText = text as string;
  }

  /** Bricht Bot-Denken und eine laufende Sprachausgabe des LiveAvatars ab (Barge-in). */
  public async stop(): Promise<void> {
    const botId = this.botidService.getBotId();
    if (!botId) return;
    const lastThread = window.RAGAI.lastThreadId;
    if (lastThread) {
      await this.botService.sendBotThinkingCancel(botId, lastThread);
    }
    if (this.attService.speakingState.getValue()) {
      this.eventManager.triggerEventHook('RAGAI.AVATAR.INTERRUPT', {});
    }
  }

  /**
   * Setzt das Hintergrundbild des Video-Avatars.
   * @param source The source can be a skill id or a url
   */
  public async setBackgroundImage(source: string) {
    if (!source.startsWith('https')) {
      source = await this.helperManager.getImageURlFromSkill(source);
    }
    const keyValEntry = await this.keyvalManager.set(this.key, source);
    this.onBackgroundUpdate.emit(keyValEntry);
  }

  /**
   * Entfernt das gesetzte Hintergrundbild des Avatars.
   * @returns Nichts.
   */
  public async unsetImage() {
    const keyValEntry = await this.keyvalManager.deleteKeyValEntry(this.key);
    this.onBackgroundDelete.emit(keyValEntry);
  }

  /**
   * Gibt die aktuell gesetzte Hintergrundbild-URL des Avatars zurück.
   * @returns Die Hintergrundbild-URL.
   */
  public async getBackgroundImage(): Promise<string> {
    return await this.keyvalManager.get(this.key) as string;
  }

  /**
   * Fügt dem Avatar-Footer (Fullscreen-Modus) einen weiteren Button hinzu (kumulativ).
   * @param button Der anzuzeigende Button.
   */
  public addFooterButton(button: AvatarFooterButton): void {
    this.footerButtons = [...this.footerButtons, button];
    this.onFooterButtonsUpdate.emit(this.footerButtons);
  }

  /** Entfernt alle aktuell gesetzten Footer-Buttons. */
  public clearFooterButtons(): void {
    this.footerButtons = [];
    this.onFooterButtonsUpdate.emit(this.footerButtons);
  }
}